import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTalosData } from 'shared/context/TalosDataContext';
import { Badge } from 'features/catalog/useBadges';
import { FeedUpdate } from 'features/feed/useFeedUpdates';
import { Profile } from 'features/profiles/useProfiles';
import { formatLastUpdated, getRankDisplay } from 'config/constants';
import { getFirstPartOfEmail } from 'shared/lib/email';
import { claimPath } from 'shared/lib/nav-paths';
import { compareBadgesByScoreAsc } from 'features/catalog/rarity';
import { BadgeThumb } from 'shared/components/BadgeThumb';
import './FeedAside.css';

interface FeedAsideProps {
  currentProfile: Profile | null;
  /** When set, standing card shows this profile instead of the logged-in user. */
  focusedProfileId?: string | null;
  /** Navigate to a profile's Feed tab. Used by standing ladder + profile open. */
  onFocusProfile: (profileId: string) => void;
  /** Open own profile feed and scroll to this activity card (or fall back to badge modal). */
  onSelectActivity?: (update: FeedUpdate, fallbackBadge: Badge | null) => void;
  /** Navigate to a profile's Feed tab. */
  onSelectProfile: (profileId: string) => void;
  setSelectedBadge?: (badge: Badge | null) => void;
  searchSlot?: React.ReactNode;
}

const getCoolestBadge = (profile: Profile, badgesMap: Map<string, Badge>): Badge | null =>
  profile.badgesIDs
    .map((id) => badgesMap.get(id))
    .filter((b): b is Badge => !!b)
    .sort(compareBadgesByScoreAsc)[0] ?? null;

const activityLabel = (update: FeedUpdate): string => {
  // Match feed cards: count badges that resolve in badgesMap (via rarity buckets), not raw IDs.
  const count =
    update.badgesByRarity.mythic.length +
    update.badgesByRarity.legendary.length +
    update.badgesByRarity.rare.length +
    update.badgesByRarity.common.length;
  if (update.isNewUser) {
    return `Joined with ${count} badge${count === 1 ? '' : 's'}`;
  }
  const badge =
    update.badgesByRarity.mythic[0] ??
    update.badgesByRarity.legendary[0] ??
    update.badgesByRarity.rare[0] ??
    update.badgesByRarity.common[0];
  if (update.isAutoGrant) {
    if (count === 1) {
      return badge ? `Granted ${badge.name}` : 'Granted 1 badge';
    }
    return `Granted ${count} badges`;
  }
  // Prefer claim wording to match default feed cards; journey-only framing is for the Journeys filter.
  if (count === 1) {
    return badge ? `Claimed ${badge.name}` : 'Claimed 1 badge';
  }
  return `Claimed ${count} badges`;
};

export const FeedAside: React.FC<FeedAsideProps> = ({
  currentProfile,
  focusedProfileId = null,
  onFocusProfile,
  onSelectActivity,
  onSelectProfile,
  setSelectedBadge,
  searchSlot,
}) => {
  const { profilesMap, rankedProfiles, badgesMap, feedUpdates } = useTalosData();
  const navigate = useNavigate();

  const standingProfile = useMemo(() => {
    if (focusedProfileId) return profilesMap.get(focusedProfileId) ?? null;
    return currentProfile;
  }, [focusedProfileId, profilesMap, currentProfile]);

  const isOwnStanding = !!currentProfile && !!standingProfile && currentProfile.email === standingProfile.email;

  const rankInfo = useMemo(() => {
    if (!standingProfile) return null;
    const index = rankedProfiles.findIndex((p) => p.email === standingProfile.email);
    if (index < 0) return null;
    return {
      rank: index + 1,
      above: index > 0 ? rankedProfiles[index - 1] : null,
      below: index < rankedProfiles.length - 1 ? rankedProfiles[index + 1] : null,
    };
  }, [standingProfile, rankedProfiles]);

  const recentActivity = useMemo(() => {
    if (!standingProfile) return [];
    return feedUpdates
      .filter(
        (u) =>
          u.profile.email === standingProfile.email ||
          !!u.grantedToProfiles?.some((p) => p.email === standingProfile.email)
      )
      .slice(0, 5);
  }, [standingProfile, feedUpdates]);

  const coolest = standingProfile ? getCoolestBadge(standingProfile, badgesMap) : null;
  const standingId = standingProfile ? getFirstPartOfEmail(standingProfile.email) : null;

  const standingTitle = isOwnStanding || !standingProfile ? 'Your standing' : `${standingProfile.name}'s standing`;
  const activityTitle =
    isOwnStanding || !standingProfile ? 'Your recent activity' : `${standingProfile.name}'s recent activity`;

  return (
    <aside className="feed-aside">
      {searchSlot}

      <section className="feed-aside-card feed-aside-standing">
        <div className="feed-aside-card-head">
          <h2 className="feed-aside-title">{standingTitle}</h2>
          <button type="button" className="feed-aside-link" onClick={() => navigate('/leaderboard')}>
            Leaderboard
          </button>
        </div>

        {!standingProfile || !rankInfo ? (
          <p className="feed-aside-empty">
            {focusedProfileId
              ? 'Couldn’t find that profile’s standing.'
              : 'Create a profile to see your leaderboard spot here.'}
          </p>
        ) : (
          <div className="feed-aside-ladder">
            {rankInfo.above ? (
              <button
                type="button"
                className="feed-aside-neighbor"
                onClick={() => onFocusProfile(getFirstPartOfEmail(rankInfo.above!.email))}
              >
                <span className="feed-aside-neighbor-rank">{getRankDisplay(rankInfo.rank - 1)}</span>
                <span className="feed-aside-neighbor-name">{rankInfo.above.name}</span>
                <span className="feed-aside-neighbor-score">{rankInfo.above.sortScore}</span>
              </button>
            ) : (
              <p className="feed-aside-neighbor-empty">{isOwnStanding ? 'You’re at the top' : 'At the top'}</p>
            )}

            <button
              type="button"
              className="feed-aside-you"
              onClick={() => standingId && onSelectProfile(standingId)}
              aria-label={isOwnStanding ? 'Open your profile' : `Open ${standingProfile.name}'s profile`}
            >
              {coolest ? (
                <BadgeThumb badge={coolest} size="lg" alt="" imageClassName="feed-aside-you-badge" />
              ) : (
                <div className="feed-aside-you-badge feed-aside-you-badge--empty" aria-hidden />
              )}
              <div className="feed-aside-you-text">
                <p className="feed-aside-you-identity">
                  <span className="feed-aside-rank">{getRankDisplay(rankInfo.rank)}</span>
                  <span className="feed-aside-you-name">{standingProfile.name}</span>
                </p>
                <p className="feed-aside-you-meta">
                  <strong>{standingProfile.sortScore}</strong> pts · {standingProfile.badgesIDs.length} badges ·{' '}
                  {standingProfile.journeyEmojis.length} journeys
                </p>
                {standingProfile.positionsMovedUp && standingProfile.positionsMovedUp > 0 ? (
                  <p className="feed-aside-moved">↑ {standingProfile.positionsMovedUp} since last update</p>
                ) : null}
              </div>
            </button>

            {rankInfo.below ? (
              <button
                type="button"
                className="feed-aside-neighbor"
                onClick={() => onFocusProfile(getFirstPartOfEmail(rankInfo.below!.email))}
              >
                <span className="feed-aside-neighbor-rank">{getRankDisplay(rankInfo.rank + 1)}</span>
                <span className="feed-aside-neighbor-name">{rankInfo.below.name}</span>
                <span className="feed-aside-neighbor-score">{rankInfo.below.sortScore}</span>
              </button>
            ) : null}
          </div>
        )}
      </section>

      <section className="feed-aside-card">
        <div className="feed-aside-card-head">
          <h2 className="feed-aside-title">{activityTitle}</h2>
          {isOwnStanding ? (
            <button
              type="button"
              className="feed-aside-link"
              onClick={() => navigate(claimPath(getFirstPartOfEmail(currentProfile.email)))}
            >
              Claim New Badges
            </button>
          ) : null}
        </div>
        {!standingProfile ? (
          <div className="feed-aside-empty-block">
            <p className="feed-aside-empty">
              {focusedProfileId
                ? 'Couldn’t find that profile’s activity.'
                : 'Create a profile to see your activity here.'}
            </p>
            {!focusedProfileId ? (
              <button type="button" className="feed-aside-create" onClick={() => navigate('/create')}>
                Create Profile
              </button>
            ) : null}
          </div>
        ) : recentActivity.length === 0 ? (
          <p className="feed-aside-empty">
            {isOwnStanding
              ? 'No recent claims in the feed window yet.'
              : `No recent claims for ${standingProfile.name} in the feed window yet.`}
          </p>
        ) : (
          <ul className="feed-aside-activity">
            {recentActivity.map((update) => {
              const topBadge =
                update.badgesByRarity.mythic[0] ??
                update.badgesByRarity.legendary[0] ??
                update.badgesByRarity.rare[0] ??
                update.badgesByRarity.common[0] ??
                null;
              return (
                <li key={`${update.eventAt.getTime()}-${update.newBadges.join(',')}`}>
                  <button
                    type="button"
                    className="feed-aside-activity-item"
                    onClick={() => {
                      if (onSelectActivity) {
                        onSelectActivity(update, topBadge);
                        return;
                      }
                      if (topBadge && setSelectedBadge) setSelectedBadge(topBadge);
                      else if (standingId) onSelectProfile(standingId);
                    }}
                  >
                    {topBadge ? (
                      <img src={topBadge.smallImageUrl} alt="" className="feed-aside-activity-badge" />
                    ) : (
                      <div className="feed-aside-activity-badge feed-aside-activity-badge--empty" aria-hidden />
                    )}
                    <div className="feed-aside-activity-text">
                      <p className="feed-aside-activity-label">{activityLabel(update)}</p>
                      <p className="feed-aside-activity-time">{formatLastUpdated(update.eventAt)}</p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </aside>
  );
};
